class CountrySelector extends HTMLElement {
    // -- SG 20261012 Rows can share a URL (Oesterreich and Deutschland both use lovetodream.de), so selection tracks the row, not its URL
    private activeItem: HTMLElement | null = null;
    private selectedItem: HTMLElement | null = null;
    private drawerOpen = false;
    private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
    private focusTrapHandler: ((e: KeyboardEvent) => void) | null = null;
    private triggerElement: HTMLElement | null = null;
    private get items(): NodeListOf<HTMLElement> {
        return this.querySelectorAll(".country__selector-drawer-location-item");
    }
    private get switchButton(): HTMLButtonElement | null {
        return this.querySelector("[data-country-switch]");
    }

    constructor() {
        super();
        this.bindEvents();
    }

    disconnectedCallback() {
        this.releaseDrawerTraps();
        if (this.drawerOpen) {
            (this.getHeader() as any)?.unlockScroll();
            this.drawerOpen = false;
        }
    }

    // -- SG 20260401 Delegate scroll lock to Header's ref-counted lock/unlock
    private getHeader(): HTMLElement | null {
        return document.querySelector("header-wrapper");
    }

    bindEvents() {
        const button = this.querySelector(".country__selector-current");
        button?.addEventListener("click", this.toggleDrawer.bind(this));
        const overlay = this.querySelector(".country__selector-drawer-overlay");
        overlay?.addEventListener("click", this.closeDrawer.bind(this));

        const backBtn = this.querySelector(".country__selector-drawer-back");
        backBtn?.addEventListener("click", this.onBackClick.bind(this));

        const closeBtn = this.querySelector(".country__selector-drawer-close");
        closeBtn?.addEventListener("click", this.onCloseClick.bind(this));

        this.switchButton?.addEventListener("click", this.onSwitchClick.bind(this));
        this.items.forEach((el) => {
            el.addEventListener("click", this.onItemClick.bind(this));
        });
    }

    private onBackClick() {
        this.closeDrawer();
    }

    private onCloseClick() {
        this.closeDrawer();
        if (this.closest(".header-content__mobile-wrapper")) {
            (this.closest("header-wrapper") as HTMLElement & { closeMobileMegaMenu?: () => void })?.closeMobileMegaMenu?.();
        }
    }

    private initState() {
        this.activeItem = this.querySelector<HTMLElement>(
            ".country__selector-drawer-location-item[data-is-current='true']"
        );
        this.selectedItem = this.activeItem;
        this.updateSelectedState();
        this.updateSwitchButtonVisibility();
    }

    private getStoreUrl(item: HTMLElement): string {
        return item.getAttribute("data-store-url") ?? "";
    }

    private isCurrentItem(item: HTMLElement): boolean {
        return item.getAttribute("data-is-current") === "true";
    }

    private updateSelectedState() {
        this.items.forEach((item) => {
            if (item === this.selectedItem) {
                item.classList.add("country__selector-drawer-location-item--selected");
            } else {
                item.classList.remove("country__selector-drawer-location-item--selected");
                if (!this.isCurrentItem(item)) {
                    item.classList.remove("country__selector-drawer-location-item--current");
                }
            }
        });
    }

    private updateSwitchButtonVisibility() {
        const btn = this.switchButton;
        if (!btn) return;
        const show = this.selectedItem !== this.activeItem;
        if (show) {
            btn.removeAttribute("hidden");
        } else {
            btn.setAttribute("hidden", "");
        }
    }

    private redirect() {
        const url = this.selectedItem ? this.getStoreUrl(this.selectedItem) : "";
        if (!url) return;
        window.location.href = url;
    }

    private onSwitchClick() {
        this.redirect();
    }

    private onItemClick(e: Event) {
        const item = e.currentTarget as HTMLElement;
        const storeUrl = this.getStoreUrl(item);

        if (!storeUrl || this.isCurrentItem(item)) {
            e.preventDefault();
            this.selectedItem = this.activeItem;
            this.updateSelectedState();
            this.updateSwitchButtonVisibility();
            return;
        }

        this.selectedItem = item;
        this.updateSelectedState();
        this.updateSwitchButtonVisibility();

        if (item instanceof HTMLAnchorElement) {
            return;
        }

        e.preventDefault();
        this.redirect();
    }

    toggleDrawer() {
        const willOpen = !this.classList.contains("open");
        this.triggerElement = document.activeElement as HTMLElement;
        this.classList.toggle("open");
        const header = this.getHeader() as any;
        if (willOpen) {
            if (!this.drawerOpen) { header?.lockScroll(); this.drawerOpen = true; }
            this.initState();
            this.installDrawerTraps();
            const closeBtn = this.querySelector(".country__selector-drawer-close") as HTMLElement;
            closeBtn?.focus();
        } else {
            this.releaseDrawerTraps();
            if (this.drawerOpen) { header?.unlockScroll(); this.drawerOpen = false; }
            this.triggerElement?.focus();
        }
    }

    closeDrawer() {
        if (!this.classList.contains("open")) return;
        this.classList.remove("open");
        this.releaseDrawerTraps();
        if (this.drawerOpen) {
            (this.getHeader() as any)?.unlockScroll();
            this.drawerOpen = false;
        }
        this.triggerElement?.focus();
    }

    // -- SG 20260401 Escape + focus trap for country drawer (A5, A9)
    private installDrawerTraps() {
        this.releaseDrawerTraps();
        const drawer = this.querySelector(".country__selector-drawer") as HTMLElement;
        this.escapeHandler = (e: KeyboardEvent) => {
            if (e.key === "Escape") { e.stopPropagation(); this.closeDrawer(); }
        };
        this.focusTrapHandler = (e: KeyboardEvent) => {
            if (e.key !== "Tab" || !drawer) return;
            const focusable = drawer.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), [tabindex="0"], input:not([disabled])'
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", this.escapeHandler);
        document.addEventListener("keydown", this.focusTrapHandler);
    }

    private releaseDrawerTraps() {
        if (this.escapeHandler) { document.removeEventListener("keydown", this.escapeHandler); this.escapeHandler = null; }
        if (this.focusTrapHandler) { document.removeEventListener("keydown", this.focusTrapHandler); this.focusTrapHandler = null; }
    }
}

customElements.define("country-selector", CountrySelector);